import { Link } from "@tanstack/react-router";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";

import { cn, Icon, IconButton, StaticTooltipHost } from "@vintecc/components";
import { faInfoCircle, faExclamationTriangle, faExclamationCircle, faCheckCircle, faXmark } from "@vintecc/pro-light-svg-icons";
import { useShallow } from "zustand/react/shallow";

import styles from "./PlatformNotification.module.css";
import { useCommonTranslation } from "@/services/culture/common.translations";
import { usePlatformNotificationsStore, type Notification } from "@/store/platformNotificationsStore";

const intentToIconMap: Record<string, IconProp> = {
  info: faInfoCircle,
  warning: faExclamationTriangle,
  error: faExclamationCircle,
  success: faCheckCircle,
};

type PlatformNotificationProps = {
  notification: Notification;
  onClose?: () => void;
};

export const PlatformNotification = ({ notification, onClose }: PlatformNotificationProps) => {
  const { t: tCommon } = useCommonTranslation();
  const { removeNotification, markAsRead } = usePlatformNotificationsStore(
    useShallow((s) => ({
      removeNotification: s.removeNotification,
      markAsRead: s.markAsRead,
    })),
  );
  let overlay: React.ReactNode = null;
  if (notification.url) {
    overlay = (
      <Link
        to={notification.url}
        params={notification.params}
        className="absolute inset-0 z-10"
        aria-label={tCommon("topbar.notificationsView")}
        onClick={() => {
          markAsRead(notification.id);
          onClose?.();
        }}
      />
    );
  } else if (notification.onClick) {
    overlay = (
      <button
        type="button"
        onClick={() => {
          notification.onClick?.();
          markAsRead(notification.id);
          onClose?.();
        }}
        className="absolute inset-0 z-10 w-full h-full appearance-none bg-transparent"
        aria-label={tCommon("topbar.notificationsOpen")}
      />
    );
  }

  return (
    <li
      key={notification.id}
      className={cn("relative p-2 ps-4 transition-colors", styles.notification, { [styles.read]: notification.read }, styles[notification.intent])}
      data-test-id={`notification-${notification.id}`}
    >
      {overlay}
      <div>
        <div className="flex gap-2 items-center">
          <div className={cn("flex gap-2 items-center flex-1 min-h-8", styles.highlight)}>
            <Icon icon={intentToIconMap[notification.intent] ?? faInfoCircle} />
            <div className={cn({ "font-bold": !notification.read })}>{notification.title}</div>
          </div>
          {notification.removable !== false && (
            <StaticTooltipHost
              tooltip={tCommon("topbar.notificationsDismiss")}
              place="top"
              customStyling={{ fill: "var(--background-navigation)", textColor: "var(--color-white)" }}
            >
              <IconButton
                className="z-20"
                size="xs"
                naked
                iconName={faXmark}
                aria-label={tCommon("topbar.notificationsDismiss")}
                onClick={() => removeNotification(notification.id)}
                type="button"
                style={{ "--icon-button-background": "transparent", "--theme-default-color": "var(--color-grey-400)" }}
              />
            </StaticTooltipHost>
          )}
        </div>
        <div className="ps-0 pe-4">{notification.message}</div>
      </div>
    </li>
  );
};
